/**
 * Keeps open copies of the app on the latest build. An installed app (iPhone home screen in
 * particular) can stay in memory for days running old code, and an old copy can fall out
 * of step with the server and with up-to-date devices, e.g. on encryption changes.
 *
 * The build publishes /version.json; when it differs from the running build, the app
 * reloads itself as soon as that's harmless (coming back to the foreground, not in a
 * call), and otherwise shows a "new version" banner.
 */
import { create } from 'zustand';
import { useVoice } from './voice';

declare const __BUILD_ID__: string;
export const BUILD_ID = __BUILD_ID__;

const CHECK_EVERY_MS = 10 * 60_000;
const RELOADED_FOR = 'reloaded-for-build';

export const useUpdate = create<{ available: string | null }>(() => ({ available: null }));

/** The build the server now serves, or null if unknown (offline, dev server). */
async function latestBuild(): Promise<string | null> {
  try {
    const res = await fetch('/version.json', { cache: 'no-store' });
    if (!res.ok) return null;
    const { id } = (await res.json()) as { id?: unknown };
    return typeof id === 'string' ? id : null;
  } catch {
    return null;
  }
}

export function isOutdated(running: string, latest: string | null): boolean {
  return !!latest && latest !== running;
}

const inCall = () => useVoice.getState().status !== 'idle';

/** Reload into the new build, at most once per build (in case the old one is still served). */
function reloadOnce(id: string): boolean {
  try {
    if (sessionStorage.getItem(RELOADED_FOR) === id) return false;
    sessionStorage.setItem(RELOADED_FOR, id);
  } catch {
    return false;
  }
  window.location.reload();
  return true;
}

/** Check now; `resumed` means the app just came to the foreground (a good time to reload). */
export async function checkForUpdate(resumed = false) {
  const latest = await latestBuild();
  if (!isOutdated(BUILD_ID, latest)) return;
  if (resumed && !inCall() && reloadOnce(latest!)) return;
  useUpdate.setState({ available: latest });
}

export function reloadNow() {
  window.location.reload();
}

let started = false;

export function startUpdateChecks() {
  if (started || BUILD_ID === 'dev') return;
  started = true;
  void checkForUpdate(true);
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible') void checkForUpdate(true);
  });
  window.setInterval(() => void checkForUpdate(), CHECK_EVERY_MS);
}
